/**
 * Reference/SKU filter component.
 *
 * Allows searching by product reference number (objectID/SKU).
 * Debounces input and filters by exact match.
 */

'use client';

import { Icon } from '@/components/ui/Icon';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './RefNumberFilter.module.scss';

interface RefNumberFilterProps {
  placeholder?: string;
  'aria-label'?: string;
}

const DEBOUNCE_DELAY = 300;

export function RefNumberFilter({ placeholder, 'aria-label': ariaLabel }: RefNumberFilterProps) {
  const t = useTranslations('plp.facets');
  const { params, setParams } = useSearchParams();
  const [inputValue, setInputValue] = useState(params.sku || '');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const displayPlaceholder = placeholder || t('referencePlaceholder');
  const displayAriaLabel = ariaLabel || t('reference');

  // Sync input with URL param on mount and when it changes externally
  // (e.g., when user clicks "Clear all filters" or uses browser back button)

  useEffect(() => {
    setInputValue(params.sku || '');
  }, [params.sku]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toUpperCase().trim();
      setInputValue(value);

      // Debounce the URL update
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        setParams({ sku: value || null, page: 1 });
      }, DEBOUNCE_DELAY);
    },
    [setParams]
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    setParams({ sku: null, page: 1 });
  }, [setParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        handleClear();
      }
    },
    [handleClear]
  );

  return (
    <div className={styles.container}>
      <input
        type="text"
        className={styles.input}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={displayPlaceholder}
        aria-label={displayAriaLabel}
      />
      {inputValue && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClear}
          aria-label={t('clear')}
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );
}
